import React from 'react';

// Responsive rules for the multi-mode widget. The standalone bundle keeps its
// shadow root otherwise unstyled (components use inline styles + host
// inheritance), so media queries -- which can't be expressed inline -- are
// emitted as a small scoped <style> rendered inside the shadow root by the
// component that needs them, NOT by injecting the global vo-search.css.
// Multi-mode widget (#5414).

// Tile grid columns come from the --vo-tile-columns custom property on desktop
// and collapse to 2 then 1 column on narrower viewports.
//
// The card rules below it are a curated mirror of the .vo-search-tile-card*
// families in vo-search.css (#8703). SearchResults renders the tile variant in
// two places, and only one of them reaches that stylesheet: the tile OVERLAY
// portals to document.body, but an INPAGE surface carrying layout=grid renders
// the same cards inside its shadow root (index.jsx:758), where the card box,
// hero and body rules reached nothing and the cards rendered unstyled. This
// block already renders in both places, so mirroring here covers the shadow
// case without a second block; in the overlay the rules simply restate what the
// stylesheet already says, at zero specificity.
//
// The grid container rule keeps its bare selector -- it mirrors nothing, since
// vo-search.css declares no .vo-search-tile-grid at all. Every selector that
// does mirror one takes the :where() convention.
export const TILE_GRID_CSS = `
.vo-search-tile-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(var(--vo-tile-columns, 3), minmax(0, 1fr));
}
@media (max-width: 768px) {
  .vo-search-tile-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 480px) {
  .vo-search-tile-grid { grid-template-columns: 1fr; }
}
:where(.vo-search-tile-card) {
  background-color: var(--background-color, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  overflow: hidden;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
:where(.vo-search-tile-card):hover {
  box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.12));
  border-color: var(--primary-color, #4b5563);
}
:where(.vo-search-tile-card-link) {
  display: block;
  height: 100%;
  color: inherit;
  text-decoration: none;
}
:where(.vo-search-tile-hero) {
  aspect-ratio: 16 / 9;
  background-color: var(--border-color, #eef0f3);
}
:where(.vo-search-tile-body) {
  padding: 12px 14px;
}
`;

// The inline dropdown sits beneath its input; on mobile it spans the full width.
//
// Two things a nav or header host needs from it:
//
//   - `--vo-search-bar-dropdown-z` (default 50, what it has always been) lifts
//     the results above the host's OWN stacking context. A sticky nav that
//     creates a stacking context with a higher z-index would otherwise paint
//     over its own search results, and a bare literal left the host nothing to
//     reach for.
//   - The host must not CLIP it. The dropdown is absolutely positioned inside
//     the widget, not portalled -- deliberately, a second portal target is a
//     larger change than this needs -- so any ancestor with `overflow` set to
//     anything but `visible` cuts it off at the host's edge. The landing's
//     .mock-site-pane is `overflow: clip`, which is exactly what that looks
//     like. Nothing the widget owns sets overflow on the dropdown's ancestors;
//     the dropdown's own `overflow: hidden` (INLINE_RESULTS_CSS) clips only its
//     own results, which is what rounds its corners.
export const INLINE_DROPDOWN_CSS = `
/* The attached surface's own root. Both declarations were an inline style
   object in inline_search.jsx until item #17191. Neither has any runtime input,
   and an inline style beats every rule in these blocks -- their selectors sit at
   specificity 0 under :where() by design (decision #5090) -- so the escape hatch
   was being spent on statics, which is item #16444's defect one element up. The
   root keeps only the custom properties themeStyleVars computes. */
.vo-search-inline {
  width: 100%;
}

/* THE ANCHOR (item #17191). This is the element the panel below is positioned
   against, and the panel is rendered INSIDE it -- so \`top: 100%\` is the bar's
   bottom edge and \`left/right: 0\` the bar's width, by construction.
   .vo-search-search-header is SearchInput's own root element, i.e. the bar.

   Both halves are load-bearing and neither survives alone. Before this item the
   containing block was \`position: relative\` inline on .vo-search-inline while
   the panel was a SIBLING of the bar, so the panel landed on the bar's extent
   only because the root, the header and the wrapper all happened to be
   width: 100% -- nothing declared the relationship, and item #17166's spec
   read the DOM wrong in three ways as a result.

   The stylesheet and INPAGE_SURFACE_CSS already give the header this same
   declaration; the attached surface renders neither, which is why it had to be
   restated here rather than assumed.

   This is the DEFAULT anchor, not the only one. Item #17166 added a second --
   .vo-search-search-input-field, which stops the panel at the submit button's
   leading edge -- selected per instance by the dropdownAnchor config key. The
   field box's own \`position: relative\` is not restated here: it lives in
   SEARCH_INPUT_CSS, where it holds the magnifier and the clear button, and this
   surface renders that block too. A second copy would be a mirror to keep in
   step for no gain, so the guard asserts it where it lives instead. */
.vo-search-search-header {
  position: relative;
}

/* Host contract: set --vo-search-bar-dropdown-z to lift these results above
   your own nav stacking context, and keep every ancestor of the search bar at
   overflow: visible -- the dropdown is positioned, not portalled, so an
   ancestor that clips cuts the results off at its edge. */
.vo-search-inline-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: var(--vo-search-bar-dropdown-z, 50);
}
@media (max-width: 480px) {
  .vo-search-inline-dropdown {
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100vw;
  }
}
`;

// Result styling for the inline mount (#8308) and the inpage surface's body,
// both of which render this block into their shadow root. Modal and tile portal
// into the light DOM, where the head-injected vo-search.css styles their
// results; a shadow root that stylesheet cannot cross would otherwise render
// results with no padding, separator, hover or type scale. This is a curated
// mirror of the result rules in vo-search.css -- the .vo-search-search-result*
// row, the .vo-search-highlighted match accent and the result-icon families
// that render inside it (#8697), and the empty, loading and error states the
// body renders in place of results (#8698) -- NOT an injection of the global
// stylesheet, which stays forbidden.
//
// The spinner's keyframes come with it: an animation name resolves against the
// tree scope its element renders in, so leaving them behind would make the
// shadow mount depend on the document defining them, which is only true on a
// page that head-injects vo-search.css. Selectors are wrapped in :where() so the block contributes zero
// specificity and component inline styles keep winning. The drift guard
// (responsive_styles.test.jsx) fails if vo-search.css grows a result rule that
// is not mirrored here, or if a mirrored rule drops or alters a declaration.
// Both files read the same theme custom properties, set on the inline container
// by themeStyleVars. Where this block deliberately departs from the stylesheet
// -- the tag pill's and the match accent's colour -- the departure and its
// reason are enumerated in the guard's DOCUMENTED_DIVERGENCES map, which is the
// one place they live.
export const INLINE_RESULTS_CSS = `
/* The dropdown is the panel's BODY dropped in on top of the page, not a box of
   its own invention: it reads the same surface background and themable surface
   border the panel does, and takes the bar's radius so the input and the
   results belong to each other. --surface-border-color is what the themed
   variants set on the panel; falling back to --border-color keeps an untinted
   embed rendering exactly as it did. */
:where(.vo-search-inline-dropdown) {
  background-color: var(--background-color, #fff);
  color: var(--text-color, #333);
  border: 1px solid var(--surface-border-color, var(--border-color, #e0e0e0));
  border-radius: var(--vo-search-bar-radius, 8px);
  box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
  /* Item #17162. none is max-height's initial value, so an instance that
     configures nothing grows to fit exactly as it did.

     This is not inlineMaxResults, which caps the row COUNT. The two are
     independent: a count of 8 in a panel tall enough for 8 shows no scrollbar,
     and a 360px cap on those 8 shows about three and scrolls. One setting
     answers "how many results exist", the other "how much of the page the
     panel may cover".

     The overflow: hidden these three lines replace was doing two jobs -- it
     clipped surplus rows AND clipped the rounded corners so the end rows do not
     square off the panel. Only the first is being changed: overflow-x keeps the
     corners, overflow-y makes the surplus reachable instead of merely cut off,
     and on an uncapped panel it never overflows so it renders no scrollbar. */
  max-height: var(--vo-search-dropdown-max-height, none);
  overflow-x: hidden;
  overflow-y: auto;
}
:where(.vo-search-search-results) {
  flex: 1 1 auto;
  position: relative;
  padding: 0;
  margin: 0;
  list-style: none;
}
:where(.vo-search-search-result) {
  /* Item #17163. Both fallbacks are today's row, so an instance that configures
     nothing does not move -- the --vo-search-bar-radius convention (#8790).

     The divider is a WIDTH rather than a second rule or a style keyword: with
     the width set to 0 this draws nothing, so one declaration keeps doing one
     job. A separate border-bottom: none rule would need its own selector, its
     own mirror in two more files, and would then have to win a source-order
     argument against this one. */
  padding: var(--vo-search-result-padding, 15px);
  border-bottom: var(--vo-search-result-divider-width, 1px) solid var(--border-color, #e5e7eb);
  cursor: pointer;
  transition: background-color 0.2s ease;
}
:where(.vo-search-search-result:last-child) {
  /* Item i22789: the pagination bar's border-top is the line under the last row. */
  border-bottom-width: 0;
}
:where(.vo-search-search-result):hover {
  background-color: var(--result-hover-bg, #f9fafb);
}
:where(.vo-search-search-result):active {
  background-color: var(--result-active-bg, #f3f4f6);
}
:where(.vo-search-search-result) a {
  text-decoration: none;
  color: inherit;
}
:where(.vo-search-search-result-link) {
  display: block;
  position: relative;
  color: inherit;
  text-decoration: none;
}
:where(.vo-search-search-result-link.has-thumb) {
  display: grid;
  grid-template-columns: var(--vo-result-thumb-width, 56px) minmax(0, 1fr);
  gap: 12px;
  align-items: stretch;
}
:where(.vo-search-search-result-text) {
  min-width: 0;
}
:where(.vo-search-result-thumbnail) {
  display: block;
  width: 100%;
  height: 100%;
  min-height: var(--vo-result-thumb-width, 56px);
  align-self: stretch;
  object-fit: cover;
  border-radius: 6px;
}
:where(.vo-search-result-thumbnail.is-empty) {
  background-color: rgba(var(--text-color-rgb, 34,34,34), 0.08);
}
:where(.vo-search-search-result-title) {
  margin: 0;
  font-size: 18px;
  color: var(--text-color, #333);
  position: relative;
  padding-right: 25px;
  font-weight: 500;
  line-height: 1.2;
}
:where(.vo-search-search-result-tags) {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 8px;
}
:where(.vo-search-search-result-tag) {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 15px;
  background-color: transparent;
  border: 1px solid var(--primary-color, #712cf9);
  color: var(--primary-color, #712cf9);
  font-size: 13px;
  font-weight: 400;
  white-space: nowrap;
}
:where(.vo-search-search-result-excerpt) {
  margin: 0;
  color: rgba(var(--text-color-rgb, 34,34,34), 0.95);
  font-size: 14px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--vo-excerpt-lines, 2);
  line-clamp: var(--vo-excerpt-lines, 2);
  overflow: hidden;
}
:where(.vo-search-highlighted) {
  /* Item #17164. themeStyleVars has emitted --highlight-color since the
     multi-mode widget landed and nothing read it: both stylesheets and this
     block read --primary-color, and search_results.jsx set the colour inline
     besides, which beat all three. Every theme declares a highlightColor and
     vo-light and vo-dark declare real values that had never reached a pixel.

     --primary-color stays as the fallback, so an instance that sets no
     highlight colour keeps rendering the accent. The token is additive; it is
     not a new default. */
  color: var(--highlight-color, var(--primary-color, #712cf9));
  text-decoration: none;
  /* Item #17165. The underline reads its OWN token first, so an instance can
     underline in yellow while the title text stays dark -- the reference's
     treatment. It falls back to --highlight-color and then to the accent, so an
     instance that sets no underline colour keeps one colour for both, which is
     today's behaviour. The chain is what expresses "follow the text colour"
     without copying a value anywhere. */
  border: 0 solid var(--highlight-underline-color, var(--highlight-color, var(--primary-color, #712cf9)));
  border-bottom-width: 2px;
  font-weight: 500;
}
:where(.vo-search-result-icons) {
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
}
:where(.vo-search-result-icon) {
  margin-left: 5px;
  display: inline-block;
  vertical-align: middle;
  position: relative;
  font-size: 14px;
}
:where(.vo-search-featured-icon) {
  color: var(--featured-icon-color, #d97706);
}
:where(.vo-search-locked-icon) {
  color: var(--locked-icon-color, #6b7280);
}
:where(.vo-search-members-icon) {
  color: var(--members-icon-color, #6b7280);
}
:where(.vo-search-loading-results) {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}
:where(.vo-search-loading-spinner) {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color, rgba(0, 0, 0, 0.1));
  border-radius: 50%;
  border-top-color: var(--primary-color, #712cf9);
  animation: vo-search-spin 1s linear infinite;
  margin-bottom: 15px;
}
@keyframes vo-search-spin {
  to {
    transform: rotate(360deg);
  }
}
:where(.vo-search-no-results) {
  padding: 40px 20px;
  text-align: center;
  color: var(--no-results-color, #888);
}
:where(.vo-search-error-message) {
  padding: 20px;
  text-align: center;
  color: var(--error-text-color, #e53e3e);
  background-color: var(--error-bg-color, #fff5f5);
  border-radius: 4px;
  margin: 20px;
}
`;

// The pager's own chrome, mirroring vo-search.css:571-612. Extracted out of
// INPAGE_SURFACE_CSS by item #16239 for the reason item #8791 extracted
// SEARCH_INPUT_CSS: SearchPagination now has two hosts and only one of them
// rendered that constant.
//
// InlineSearch -- the attached surface -- mounts into a shadow root the
// head-injected vo-search.css cannot reach, so a block that lives only in
// INPAGE_SURFACE_CSS is simply absent there and the component computes to UA
// defaults (decision #5090). It cannot render INPAGE_SURFACE_CSS instead: that
// constant sizes .vo-search-search-modal and .vo-search-search-header, and an
// attached bar has neither.
//
// INPAGE_SURFACE_CSS composes this rather than dropping it, so the modal/inpage
// hosts render exactly the bytes they rendered before and their drift guard
// still checks the rules it was written to check.
export const PAGINATION_CSS = `
:where(.vo-search-pagination-container) {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px;
  gap: 10px;
  border-top: 1px solid var(--border-color, #e0e0e0);
}
:where(.vo-search-pagination) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
:where(.vo-search-pagination-button) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border-radius: 4px;
  border: none;
  background-color: var(--pagination-button-bg-color, var(--primary-color, #4b5563));
  color: var(--pagination-icon-color, #fff);
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}
:where(.vo-search-pagination-button:disabled),
:where(.vo-search-pagination-button.vo-search-disabled) {
  opacity: 0.25;
  cursor: not-allowed;
}
:where(.vo-search-pagination-arrow) {
  font-size: 18px;
  color: var(--pagination-icon-color, #fff);
}
`;

// Surface styling for the inpage mount (decision #5050). The three other
// renderers already reach the head-injected vo-search.css: modal and tile
// portal out to document.body, and the inline mount's results are mirrored
// above. The inpage surface returns in place -- it is defined by sitting in
// the document flow, so portaling it out is the one thing it cannot do -- and
// inside a shadow root it was left with no surface box, no height cap and no
// internal scroller at all, so the result list sized its own container.
//
// This is a curated mirror of the surface, header, body, footer and
// attribution rules in vo-search.css -- NOT an injection of the global
// stylesheet, which stays forbidden. Selectors are wrapped in :where() so the
// block contributes zero specificity and component inline styles keep winning.
// The drift guard (responsive_styles.test.jsx) fails if vo-search.css grows a
// surface rule that is not mirrored here, or if a mirrored rule drops or alters
// a declaration. Both files read the same theme custom properties, set on the
// surface by themeStyleVars.
//
// This block departs from vo-search.css in three places -- the surface's
// `--primary-color` fallback, its open/close `transition`, and the accent the
// input border, footer hover and attribution link resolve. Each departure and
// its reason are enumerated in the guard's DOCUMENTED_DIVERGENCES map, which is
// the one place they live; the guard fails on any departure not listed there,
// and on any listed departure the mirror no longer takes.
//
// The height pair is mirrored as the cascade produces it in light DOM: the
// base rule's 80vh, then the in-flow override. That override is spelled
// verbatim, `:not(.vo-search-modal-enabled)` and all -- item #8353 rekeyed it
// to the positive .vo-search-inpage class, and item #8679 put it back once the
// overlay families stopped being exempt from selector parity. The two forms
// select the same element in every shadow mount (the inline dropdown never
// renders .vo-search-search-modal at all), so the verbatim one costs nothing
// and is the one the drift guard can actually check.
export const INPAGE_SURFACE_CSS = `
:where(.vo-search-search-modal) {
  background-color: var(--background-color, #fff);
  color: var(--text-color, #374151);
  border-radius: 8px;
  box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.08));
  border: 1px solid var(--border-color, #e5e7eb);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 80vh;
  --header-height: 75px;
  --footer-height: 75px;
}
:where(.vo-search-search-modal:not(.vo-search-modal-enabled)) {
  height: var(--vo-search-surface-height, 380px);
}
:where(.vo-search-search-header) {
  padding: 15px;
  border-bottom: 1px solid var(--border-color, #e0e0e0);
  position: relative;
  flex: 0 0 var(--header-height);
  background-color: var(--background-color, #fff);
  color: var(--text-color, #222);
  z-index: 2;
}
:where(.vo-search-search-body) {
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: var(--background-color, #fff);
  color: var(--text-color, #222);
  max-height: calc(80vh - var(--header-height) - var(--footer-height));
  min-height: 200px;
  overflow: hidden;
  flex: 1 1 auto;
  overflow-y: auto;
}
${PAGINATION_CSS}
:where(.vo-search-search-footer) {
  padding: 12px 16px;
  border-top: 1px solid var(--border-color, #e0e0e0);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 48px;
  box-sizing: border-box;
  background-color: var(--background-color, #fff);
  color: var(--text-color, #c5c8cc);
  font-size: 15px;
}
:where(.vo-search-search-footer a) {
  text-decoration: none;
  cursor: pointer;
}
:where(.vo-search-search-footer a:hover) {
  color: var(--primary-color, #712cf9);
}
:where(.vo-search-attribution) {
  color: var(--text-color, #888);
  font-size: 13px;
  display: flex;
  align-items: center;
}
:where(.vo-search-attribution *) {
  text-decoration: none;
  color: inherit;
}
:where(.vo-search-attribution-link) {
  font-weight: 500;
  margin-left: 5px;
  color: var(--primary-color, #712cf9);
}
:where(.vo-search-attribution-link:hover) {
  text-decoration: underline;
}
`;

// The search input's own chrome, mirroring vo-search.css:355-388. Extracted out
// of INPAGE_SURFACE_CSS by item #8791 because SearchInput has four hosts and
// only three of them rendered that constant.
//
// InlineSearch -- the attached surface, which is what the dashboard's shipped
// Search Bar preset resolves to -- rendered the reset and the results blocks and
// nothing else. In the light DOM vo-search.css supplied these rules, so the gap
// was invisible until the surface moved into a shadow root; inside one, nothing
// declared the input's background or colour and the UA white/black won over a
// theme that computed correctly and inherited all the way to the element
// (decision #5090).
//
// It could not just render INPAGE_SURFACE_CSS instead: that constant gives
// .vo-search-search-header a 75px flex basis, padding and a bottom border, and
// SearchInput's root element IS .vo-search-search-header -- an attached bar would
// grow a boxed 75px header.
//
// Nor does SearchInput render this itself, which looks like the composition-proof
// answer. Its style tag would land after every surface block INCLUDING
// THEME_VARIANT_CSS, and since all of these sit at specificity 0 under :where()
// source order is the whole cascade -- it would silently take the glass wrapper
// border back off the modal. Every host renders it explicitly, and the geometry
// gate is what catches a host that forgets (decision #5090, per #5081).
//
// So the ordering constraint travels with it: this block goes AFTER the reset,
// whose `margin: 0` the wrapper's `margin: 0` agrees with anyway, and BEFORE
// THEME_VARIANT_CSS, whose themed 1px wrapper border must keep winning the
// zero-specificity tie against the 2px accent border below.
export const SEARCH_INPUT_CSS = `
/* --vo-search-bar-inner-radius is the radius of this box's PADDING corner --
   the hole the input and the submit button sit in -- which is the wrapper's own
   radius less the width of the border drawn around it. It has to be computed
   because both terms move: the radius is host-tunable and the themed variants
   drop the border to 1px.

   \`overflow: hidden\` used to make the subtraction unnecessary by clipping the
   leaves to that corner, and it was removed here (item #17191) because it was
   doing two unrelated jobs -- rounding corners and clipping content -- and only
   the first was wanted. The second is what would cut off the attached panel
   once item #17166 moves it into the field box. Same conflation item #17162
   split on the panel itself, one level up. */
:where(.vo-search-search-input-wrapper) {
  --vo-search-bar-inner-radius: max(0px, calc(var(--vo-search-bar-radius, 8px) - var(--vo-search-bar-border-width, 2px)));
  position: relative;
  border: var(--vo-search-bar-border-width, 2px) solid var(--primary-color, #712cf9);
  border-radius: var(--vo-search-bar-radius, 8px);
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
}
:where(.vo-search-search-input-wrapper:focus-within) {
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb, 113, 44, 249), 0.25);
}
:where(.vo-search-search-input-field) {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}
:where(.vo-search-search-submit) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 0 0 auto;
  align-self: stretch;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 0 16px;
  font-family: inherit;
  font-size: var(--vo-search-submit-font-size, 15px);
  font-weight: 600;
  white-space: nowrap;
  color: var(--vo-search-submit-color, #fff);
  background: var(--vo-search-submit-fill, var(--primary-color, #712cf9));
  border-start-end-radius: var(--vo-search-bar-inner-radius);
  border-end-end-radius: var(--vo-search-bar-inner-radius);
}
:where(.vo-search-search-submit:focus-visible) {
  box-shadow: inset 0 0 0 2px var(--vo-search-submit-color, #fff);
}
:where(.vo-search-search-input) {
  flex: 1;
  border: none;
  /* The leading padding is clearance for the magnifier, which is optional
     (item #16449). The property is emitted only when the glyph is OFF, so an
     instance that configures nothing resolves to the 40px this declaration
     hardcoded before. */
  padding: 12px 0 12px var(--vo-search-input-leading-padding, 40px);
  font-family: inherit;
  font-size: var(--vo-search-input-font-size, 18px);
  height: var(--vo-search-input-height, auto);
  outline: none;
  width: 100%;
  border-width: 2px;
  background-color: var(--input-background-color, #fff);
  color: var(--input-text-color, #222);
  border-start-start-radius: var(--vo-search-bar-inner-radius);
  border-end-start-radius: var(--vo-search-bar-inner-radius);
}
/* The submit button is optional and off by default, so on most instances the
   input IS the whole field box and has to round the trailing end too.
   \`:last-child\` reads that off the DOM -- the button is either rendered after
   the field box or it is not -- rather than off a second copy of the config
   flag that decides it. */
:where(.vo-search-search-input-field:last-child) :where(.vo-search-search-input) {
  border-start-end-radius: var(--vo-search-bar-inner-radius);
  border-end-end-radius: var(--vo-search-bar-inner-radius);
}
:where(.vo-search-search-input)::placeholder {
  color: var(--input-placeholder-color, #9ca3af);
  opacity: 1;
}
:where(.vo-search-search-input:focus) {
  border-color: var(--primary-color, #712cf9);
  box-shadow: none;
}
`;

// Trigger styling for the icon trigger (decision #5064). The trigger is the one
// control that renders in place: the overlay it opens portals out to
// document.body and reaches the head-injected vo-search.css, but the button
// itself stays inside the pinned shape's shadow root, where that stylesheet
// cannot reach. Unmirrored it fell all the way back to the UA default button --
// a 36x26 rgb(239,239,239) box with a 2px outset border and cursor:default,
// which is the "gray box with a white icon" reported on /app/search/3339. The
// icon read correctly throughout only because its stroke is an inline SVG
// attribute rather than a CSS rule.
//
// This is a curated mirror of the .vo-search-search-modal-trigger rules in
// vo-search.css -- NOT an injection of the global stylesheet, which stays
// forbidden. Selectors are wrapped in :where() so the block contributes zero
// specificity and component inline styles keep winning. The drift guard
// (responsive_styles.test.jsx) fails if vo-search.css grows a reachable trigger
// rule that is not mirrored.
//
// Mirrored verbatim, `top` and `margin-right` included, so a pinned slot renders
// exactly as it did before decision #5050 routed it through a shadow root and it
// was built by createTriggerButton in the light DOM. Unlike the surface
// families, these rules read no unfallbacked Bootstrap variable, so no
// divergence is needed.
//
// Several trigger families and variants in the stylesheet are deliberately not
// mirrored, because nothing can match them inside a shadow root. Which ones,
// and why, are stated in the guard's UNREACHABLE_IN_SHADOW map and its selector
// patterns -- the guard now works from the complement, so a family that is
// neither mirrored nor listed there fails the suite.
export const TRIGGER_CSS = `
:where(.vo-search-search-modal-trigger) {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--trigger-size, 20px);
  height: var(--vo-search-trigger-height, var(--trigger-size, 20px));
  border-radius: var(--trigger-border-radius, 0);
  border: var(--trigger-border-width, 0) solid transparent;
  background-color: var(--trigger-background, transparent);
  box-shadow: var(--trigger-shadow, none);
  cursor: pointer;
  transition: transform 0.2s ease;
  padding: 0;
  margin: 0;
  top: var(--vo-search-trigger-offset-block, 2px);
  margin-right: var(--vo-search-trigger-margin-inline-end, 10px);
}
:where(.vo-search-search-modal-trigger:hover) {
  transform: var(--trigger-hover-transform, scale(1.05));
  background-color: var(--trigger-hover-background, transparent);
  box-shadow: var(--trigger-hover-shadow, none);
}
:where(.vo-search-search-modal-trigger:active) {
  transform: var(--trigger-active-transform, scale(0.98));
  box-shadow: var(--trigger-active-shadow, none);
}
:where(.vo-search-search-modal-trigger svg) {
  width: var(--trigger-icon-size, 30px);
  height: var(--trigger-icon-size, 30px);
  color: var(--modal-trigger-icon-color, #ffffff);
  fill: currentColor;
  transition: color 0.2s ease, fill 0.2s ease;
  max-width: 100%;
  max-height: 100%;
}
`;

// Overlay chrome for a placement whose overlay is scoped to itself (item
// #8679). The overlay was the one surface that never needed a mirror: decision
// #5064's test is whether an element portals out to document.body, and until
// #8677/#8678 every overlay did. A placement configured overlayScope:"self"
// keeps its overlay inside its own shadow root instead, and the moment it does
// none of these rules arrive -- the container loses its fixed full-viewport box,
// the backdrop its tint and fade, the surface its centring, and the close button
// falls all the way back to the UA button.
//
// This is a curated mirror of the overlay's own families in vo-search.css --
// the container, the backdrop, the modal-enabled positioning pair, the titlebar
// and close button, and the tile layout's box -- NOT an injection of the global
// stylesheet, which stays forbidden. The surface, result, input, footer and
// pagination families the overlay renders INSIDE that chrome are already
// mirrored by INPAGE_SURFACE_CSS and INLINE_RESULTS_CSS; a scoped overlay
// renders those blocks alongside this one rather than restating them.
//
// Selectors are :where()-wrapped for zero specificity, descendant halves
// included, so the block loses every tie to a component's inline styles exactly
// as the other mirrors do.
//
// The `!important` declarations in the media block are the stylesheet's, kept
// verbatim. They are how the fullscreen phone layout beats the centred-card
// transform inline styles, and dropping them would change what renders rather
// than where it renders -- which is the one thing decision #5067 says scoping
// must not do.
export const SCOPED_OVERLAY_CSS = `
:where(.vo-search-modal-container) {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
:where(.vo-search-modal-enabled) :where(.vo-search-search-modal) {
  transform: translate(-50%, -50%) scale(0.75);
  opacity: 0;
  pointer-events: none;
  position: absolute;
  top: 50%;
  left: 50%;
  /* Host-relative, matching the stylesheet: a scoped overlay's card resolves
     100% against its placement box, so a 579px host gets a card that fits
     instead of a 600px one the host clips. */
  width: min(100%, 600px);
}
:where(.vo-search-modal-enabled) :where(.vo-search-search-modal.vo-search-active) {
  transform: translate(-50%, -50%) scale(1);
  opacity: 1;
  pointer-events: auto;
}
:where(.vo-search-search-modal-backdrop) {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--backdrop-color, rgba(0, 0, 0, 0.7));
  backdrop-filter: blur(3px);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}
:where(.vo-search-search-modal-backdrop.vo-search-active) {
  opacity: 1;
  pointer-events: auto;
}
:where(.vo-search-search-titlebar) {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 10px 15px;
  background-color: var(--background-color, #fff);
  border-bottom: 1px solid var(--border-color, #e0e0e0);
  position: relative;
}
:where(.vo-search-search-close-button) {
  background: transparent;
  border: none;
  padding: 5px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-color, #333);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}
:where(.vo-search-search-close-button:hover) {
  background-color: rgba(var(--text-color-rgb, 34,34,34), 0.05);
}
:where(.vo-search-search-close-button) svg {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  stroke-width: 2px;
  transition: stroke 0.2s ease;
}
:where(.vo-search-tile-overlay-inner) {
  background-color: var(--background-color, #fff);
  color: var(--text-color, #374151);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.08));
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  height: 90vh;
}
/*
  Containment (item #8711). Everything above this point is mirrored from
  vo-search.css declaration for declaration; the rules below are the mirror
  ADDING what the stylesheet has no counterpart for, which is the one direction
  the drift guard allows. They are keyed on .vo-search-overlay-scoped, a class
  only a scoped overlay carries, so a page-scoped overlay in the wild cannot
  reach them.

  A shadow root is not a containing block, so position: fixed kept measuring
  the viewport from inside the placement -- the overlay moved into its panel and
  went on covering the page. position: absolute against a positioned host is
  what actually contains it. Per decision #5067 this is still only where the
  overlay's box is laid out: same shape, same closed-until-triggered.

  The positioned host these resolve against is the placement's own mount point,
  which mountPlacement positions when it scopes a placement to itself -- there,
  where the portal target is created, rather than from a :host rule here that
  would only reach a target that happens to sit in a shadow root.
*/
:where(.vo-search-modal-container.vo-search-overlay-scoped) {
  position: absolute;
}
:where(.vo-search-overlay-scoped) :where(.vo-search-search-modal-backdrop) {
  position: absolute;
}
/*
  The card sizes itself in viewport units (height: 80vh), which is a second way
  out of the host and one the container's box cannot close: a panel shorter than
  80% of the window would have the card hanging out of it. max-height beats
  height, so the card stops where its host does.
*/
:where(.vo-search-overlay-scoped) :where(.vo-search-search-modal) {
  max-height: 100%;
}
@media (max-width: 600px), (max-height: 600px) {
  :where(.vo-search-modal-enabled) :where(.vo-search-search-modal) {
    transform: none !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    position: fixed !important;
    transition: opacity 0.3s ease;
    max-width: none !important;
    max-height: none !important;
    border-radius: 0 !important;
    border: none !important;
    margin: 0 !important;
  }
  :where(.vo-search-modal-enabled) :where(.vo-search-search-modal.vo-search-active) {
    transition: opacity 0.3s ease;
    transform: none !important;
  }
  /* Compound, matching the stylesheet: the container carries the class itself,
     so the descendant form this mirrored matched nothing. */
  :where(.vo-search-modal-container.vo-search-modal-enabled) {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
    overflow-y: auto;
  }
  :where(.vo-search-modal-enabled) :where(.vo-search-search-modal-content) {
    height: 100vh;
    width: 100vw;
    max-width: 100vw;
    margin: 0;
    padding: 0;
    border-radius: 0;
    display: flex;
    flex-direction: column;
  }
  :where(.vo-search-modal-enabled) :where(.vo-search-search-body) {
    max-height: calc(100vh - var(--header-height) - var(--footer-height));
    min-height: 0;
    flex: 1;
  }
  :where(.vo-search-modal-enabled) :where(.vo-search-search-results) {
    padding-bottom: 20px;
  }
  :where(.vo-search-modal-enabled) :where(.vo-search-search-modal-backdrop) {
    background-color: rgba(0, 0, 0, 0.85);
  }
  :where(.vo-search-modal-enabled) :where(.vo-search-search-titlebar),
  :where(.vo-search-modal-enabled) :where(.vo-search-search-header) {
    padding: 10px;
    border-bottom-color: var(--border-color, rgba(0, 0, 0, 0.1));
  }
  :where(.vo-search-modal-enabled) :where(.vo-search-search-close-button) {
    width: 28px;
    height: 28px;
  }
  :where(.vo-search-modal-enabled) :where(.vo-search-search-close-button) svg {
    width: 18px;
    height: 18px;
  }
  :where(.vo-search-modal-enabled) :where(.vo-search-search-footer) {
    height: auto;
    min-height: 48px;
    padding: 12px;
  }
  /*
    The phone layout pins the modal to the viewport with position: fixed
    !important, and a media query measures the VIEWPORT -- so a laptop under
    600px tall matches it on an ordinary desktop page and a contained overlay
    breaks straight back out. Scoped, it stays inside its host at every width.
    The !important is the stylesheet's own, matched so this can beat it.
  */
  :where(.vo-search-overlay-scoped) :where(.vo-search-search-modal) {
    position: absolute !important;
  }
}
`;

// Shadow-root reset for every mount that renders inside one: the inpage
// surface, the inline dropdown, and the two overlay roots a scoped placement
// keeps in its own shadow root.
//
// The mirrors above reproduce every rule vo-search.css declares. The light-DOM
// path also leans on something the stylesheet does NOT declare -- the host
// page's global reset. `box-sizing` is not an inherited property, so nothing
// about being inside a shadow root can pick it up, and every element falls back
// to the UA `content-box` default. That is not a mirroring gap that another
// curated rule can close; it is the reset itself going missing, so it is
// supplied here by name.
//
// Measured on /app/search/3339/preview before this block existed, against the
// same widget rendering in light DOM on the landing demo:
//   - .vo-search-search-header  106px vs 75px  (flex-basis 75 + 30 padding + 1 border)
//   - .vo-search-search-input-wrapper  68px vs 44px, and width:100% + border:2px
//     overflowing its container by 4px
//   - .vo-search-search-result  108px vs ~71px for the same single-line title,
//     from the UA `h3 { margin: 1em 0 }` no stylesheet overrides
//
// `margin: 0` is the second half of the same reset. It covers the widget's own
// text elements -- the result title and the unclassed <p>Searching...</p> /
// <p>No results found</p> in search_results.jsx -- exactly as every host reset
// this widget renders against already does.
//
// Specificity is 0: each selector is :where()-rooted and its descendant half is
// the universal selector. That is deliberate and it is why this block MUST be
// rendered BEFORE the mirrors -- a reset wins nothing on specificity and loses
// every tie on source order, so `margin-bottom` on the tag row, `margin-left`
// on the attribution link and friends keep applying. The render sites assert
// that ordering.
// The overlay roots are the surface's ANCESTORS, not its descendants -- the
// container, its backdrop and its content wrapper sit outside
// .vo-search-search-modal, and the tile layout is a sibling shape entirely --
// so resetting the surface leaves them on the UA default. vo-search.css resets
// the same three roots for the light-DOM overlay (item #8705);
// .vo-search-tile-overlay is shadow-only, because the stylesheet declares
// nothing for it at all and the light-DOM copy rides the host page's reset.
// .vo-search-inline is the attached surface's own root (inline_search.jsx). Its
// input wrapper is a descendant of it and of nothing else listed here, so until
// it was named the bar's wrapper fell back to the UA content-box while the
// panel's was border-box: 380px inside a 376px host, 68px tall against the
// panel's 42px. Those are the same numbers the comment above enumerates -- the
// defect was fixed for the panel and missed for the bar.
//
// `text-align: initial` is the third half of the reset and it repeats the same
// lesson in a different property (item #17159). box-sizing and margin are the
// two the box model needed; text-align is INHERITED, so it crosses the shadow
// boundary from the host element and no amount of resetting descendants would
// have stopped it -- the value arrives at the root. The Diff's search sits in a
// centred card, which is why our suggestion rows rendered centre-aligned
// against a left-aligned reference. No host should be able to re-align our
// result text by accident.
//
// Roots ONLY, deliberately, unlike the pair above. The box model is safe to
// force on every descendant because nothing inherits it; alignment is not, and
// the widget centres on purpose (.vo-search-no-results, .vo-search-error-message
// and .vo-search-search-title). Naming only the roots lets the host's value stop
// at the boundary and the widget's own cascade flow normally underneath.
//
// `initial`, not `left`: the initial value of text-align is `start`, which
// follows the writing direction. Hardcoding `left` would re-align an RTL
// instance's results to the wrong edge -- the same defect this fixes, pointing
// the other way.
//
// Shadow only, and the asymmetry with vo-search.css's light-DOM reset below is
// deliberate rather than an omission. That reset covers the same three overlay
// roots for the box model, because box-sizing is not inherited and forcing it
// is safe anywhere. Alignment is not the same case: a shadow boundary is a
// containment boundary by design, so stopping the host's value there restores
// what the author already expected, while the light-DOM inpage surface sits in
// the customer's own markup and a customer who centres the section they embed
// us in is asking for centred text. Containing it there would override an
// intention rather than an accident.
export const SHADOW_SURFACE_RESET_CSS = `
:where(.vo-search-search-modal),
:where(.vo-search-search-modal) *,
:where(.vo-search-inline),
:where(.vo-search-inline) *,
:where(.vo-search-inline-dropdown),
:where(.vo-search-inline-dropdown) *,
:where(.vo-search-modal-container),
:where(.vo-search-modal-container) *,
:where(.vo-search-tile-overlay),
:where(.vo-search-tile-overlay) *,
:where(.vo-search-tile-overlay-inner),
:where(.vo-search-tile-overlay-inner) * {
  box-sizing: border-box;
  margin: 0;
}
:where(.vo-search-search-modal),
:where(.vo-search-inline),
:where(.vo-search-inline-dropdown),
:where(.vo-search-modal-container),
:where(.vo-search-tile-overlay),
:where(.vo-search-tile-overlay-inner) {
  text-align: initial;
}
`;

// Glass treatment for the two VO themes (item #8704). index.jsx:720 sets
// data-theme on the surface div itself, so for a shadow mount these selectors
// match INSIDE the shadow root -- and the stylesheet's copy reached nothing
// there, so a customer on either vo theme kept the blur and glow in the modal
// (which portals out) and lost them in the surface.
//
// themeStyleVars cannot substitute for this. It sets custom properties only,
// and backdrop-filter and the layered box-shadow are properties: there is no
// variable the surface could read that would produce them.
//
// This is its own block, and it MUST be rendered LAST, after every mirror it
// overrides. vo-search.css expresses the override with specificity -- these
// selectors carry a class and an attribute, the base rules one class -- but
// the mirrors are all :where()-wrapped at specificity 0, so source order is the
// only thing left to express it with. Rendered before INPAGE_SURFACE_CSS the
// themed surface would keep the plain border, and before INLINE_RESULTS_CSS
// the themed row feedback would lose to the base hover. The render site
// asserts that ordering.
export const THEME_VARIANT_CSS = `
:where(.vo-search-search-modal[data-theme="vo-dark"]) {
  backdrop-filter: blur(var(--backdrop-blur, 16px));
  -webkit-backdrop-filter: blur(var(--backdrop-blur, 16px));
  box-shadow:
    var(--shadow-lg, 0 20px 40px rgba(139, 79, 255, 0.2)),
    0 0 60px rgba(139, 79, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  border: 1px solid var(--surface-border-color, rgba(139, 79, 255, 0.35));
}
:where(.vo-search-search-modal[data-theme="vo-dark"]) :where(.vo-search-search-input-wrapper),
:where(.vo-search-inline[data-theme="vo-dark"]) :where(.vo-search-search-input-wrapper) {
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  --vo-search-bar-border-width: 1px;
  border: var(--vo-search-bar-border-width, 2px) solid rgba(139, 79, 255, 0.2);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}
:where(.vo-search-search-modal[data-theme="vo-dark"]) :where(.vo-search-search-result):hover,
:where(.vo-search-inline[data-theme="vo-dark"]) :where(.vo-search-search-result):hover {
  background-color: var(--result-hover-bg, rgba(139, 79, 255, 0.08));
}
:where(.vo-search-search-modal[data-theme="vo-dark"]) :where(.vo-search-search-result):active,
:where(.vo-search-inline[data-theme="vo-dark"]) :where(.vo-search-search-result):active {
  background-color: var(--result-active-bg, rgba(139, 79, 255, 0.12));
}
:where(.vo-search-search-modal[data-theme="vo-light"]) {
  backdrop-filter: blur(var(--backdrop-blur, 12px));
  -webkit-backdrop-filter: blur(var(--backdrop-blur, 12px));
  box-shadow:
    var(--shadow-lg, 0 15px 35px rgba(113, 44, 249, 0.12)),
    0 0 40px rgba(113, 44, 249, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  border: 1px solid var(--surface-border-color, rgba(113, 44, 249, 0.2));
}
:where(.vo-search-search-modal[data-theme="vo-light"]) :where(.vo-search-search-input-wrapper),
:where(.vo-search-inline[data-theme="vo-light"]) :where(.vo-search-search-input-wrapper) {
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  --vo-search-bar-border-width: 1px;
  border: var(--vo-search-bar-border-width, 2px) solid rgba(113, 44, 249, 0.15);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
}
:where(.vo-search-search-modal[data-theme="vo-light"]) :where(.vo-search-search-result):hover,
:where(.vo-search-inline[data-theme="vo-light"]) :where(.vo-search-search-result):hover {
  background-color: var(--result-hover-bg, rgba(113, 44, 249, 0.04));
}
:where(.vo-search-search-modal[data-theme="vo-light"]) :where(.vo-search-search-result):active,
:where(.vo-search-inline[data-theme="vo-light"]) :where(.vo-search-search-result):active {
  background-color: var(--result-active-bg, rgba(113, 44, 249, 0.08));
}
`;

// ResponsiveStyles renders a scoped <style> element carrying the given CSS.
// Rendered inside a shadow root, its rules apply only to that shadow root.
export function ResponsiveStyles({ css }) {
  return <style>{css}</style>;
}

// The instance's own stylesheet (item #17981, decision #6131): customCss on the
// public config, rendered as the LAST style element in every root the widget
// mounts. Every curated block above sits at specificity 0 under :where()
// (decision #5090), so an instance selector wins by source order alone -- no
// !important here, and no change to any mirror. Inside a page-scoped overlay
// it lands in the light DOM after the head-injected vo-search.css, where a
// later <style> wins the ties at equal specificity.
//
// This is what lets a customer's theme reach inside the shadow boundary as one
// artifact instead of one config toggle per measurement; the toggles could
// never carry a media query. Empty renders nothing, so an instance that
// configures no stylesheet ships exactly the blocks it did before. The data
// attribute is the marker tests and the bundle guard look for.
export function InstanceStyles({ css }) {
  if (!css) return null;
  return <style data-vo-search-instance-styles="">{css}</style>;
}

// Every block a scoped overlay needs, in the one order that works. Both portal
// sites -- the modal in index.jsx and the tile overlay in tile_overlay.jsx --
// render this rather than listing the blocks themselves, so they cannot drift
// apart on which mirrors ship or in what order.
//
// The order is the whole point, because every block sits at specificity 0 and
// source order is the only thing left to rank them with:
//   - the reset goes FIRST, so its `margin: 0` loses to the tag row's
//     margin-bottom and the attribution link's margin-left;
//   - SCOPED_OVERLAY_CSS goes AFTER INPAGE_SURFACE_CSS, so the overlay's
//     absolute centring wins over the surface's in-flow box;
//   - SEARCH_INPUT_CSS goes BEFORE THEME_VARIANT_CSS, so the themed 1px glass
//     wrapper border keeps winning over its 2px accent border (item #8791);
//   - THEME_VARIANT_CSS goes LAST, so the glass treatment wins over the base
//     surface and the base row hover, as its own comment requires.
//   - the instance stylesheet goes after THEME_VARIANT_CSS, so the customer's
//     own rules are the final word (item #17981).
export function ScopedOverlayStyles({ customCss = '' } = {}) {
  return (
    <>
      <ResponsiveStyles css={SHADOW_SURFACE_RESET_CSS} />
      <ResponsiveStyles css={INPAGE_SURFACE_CSS} />
      <ResponsiveStyles css={SEARCH_INPUT_CSS} />
      <ResponsiveStyles css={INLINE_RESULTS_CSS} />
      <ResponsiveStyles css={SCOPED_OVERLAY_CSS} />
      <ResponsiveStyles css={THEME_VARIANT_CSS} />
      <InstanceStyles css={customCss} />
    </>
  );
}
